<script>
import {ref, toRef} from "vue";
import {IMaskDirective} from 'vue-imask';

import IconX from "@js/components/Icons/IconX.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import {useCallbackStore} from "@js/store/CallbackStore.js";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import IconAnimatedCheckMark from "@js/components/Icons/IconAnimatedCheckMark.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import baseImg from '@img/callback/base.webp'
import {useSwipePanel} from "@js/useSwipePanel.js";
import IconLogoSquare from "@js/components/Icons/IconLogoSquare.vue";

export default {
    name: "BaseCallback",
    components: {
        IconLogoSquare,
        ImageLoader,
        IconAnimatedCheckMark,
        OnlyDesktop,
        PrivacyPolicyAgreementLink,
        SpinnerIndicator,
        CallbackErrorMessage,
        IconX,
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            phoneMask: {
                mask: '+7 (000) 000-00-00',
            },
        }
    },
    methods: {
        updatePhone(newPhone) {
            this.callbackStore.base_form.data.phone = newPhone;
        },
    },
    setup() {
        const callbackStore = useCallbackStore()
        const modalRef = ref(null)
        const dragHandleRef = ref(null)

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen: toRef(callbackStore.base_form, 'isOpen'),
            panelRef: modalRef,
            handleRef: dragHandleRef,
            direction: 'bottom',
            closeThreshold: 110,
            onClose: () => {
                callbackStore.closeCallbackPopup()
            },
        })

        return {
            callbackStore,
            modalRef,
            dragHandleRef,
            rendered,
            panelStyle,
            backdropStyle,
            closeModal: close,
            handleBackgroundClick,
            baseImg,
        }
    },
}
</script>

<template>
    <div
        v-if="rendered"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click="handleBackgroundClick"
    >
        <div
            class="absolute inset-0 bg-black/70"
            :style="backdropStyle"
        ></div>

        <div
            ref="modalRef"
            :style="panelStyle"
            class="relative z-[1] bg-white bg-contain bg-no-repeat bg-center rounded-tr-2xl rounded-tl-2xl sm:rounded-2xl shadow-lg max-h-[70%] sm:max-w-[800px] sm:max-h-[450px] w-full h-full overflow-hidden"
            @click.stop
        >
            <button
                @click.prevent="closeModal"
                class="absolute top-2 right-2 text-gray-400 md:hover:text-white focus:outline-none z-20"
            >
                <IconX
                    class="h-7 w-7 stroke-[#000] active:stroke-baseColor sm:hover:stroke-baseColor transition"
                />
            </button>

            <div class="absolute top-3 right-0 left-0 m-auto z-10 flex justify-center lg:hidden">
                <div
                    ref="dragHandleRef"
                    class="h-8 w-24 flex items-start justify-center select-none"
                    style="touch-action: none; user-select: none;"
                >
                    <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
                </div>
            </div>

            <div class="w-full h-full flex items-start sm:items-center justify-center sm:justify-between pt-16 sm:pt-0">
                <OnlyDesktop :maxSize="1024" class="w-[400px] h-full">
                    <div class="relative h-full w-full overflow-hidden bg-[#111] p-8 text-white">
                        <div class="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,186,0,.28),transparent_34%),linear-gradient(135deg,#1b1b1b,#090909)]"></div>

                        <div class="relative z-[1] flex h-full flex-col justify-between">
                            <div>
                                <IconLogoSquare class="mb-5 w-10 h-10" icon_bottom_color="#fff"/>

                                <h3 class="text-3xl font-bold leading-tight">
                                   Ваш поставщик<br/> для стройки
                                </h3>

                                <p class="mt-4 max-w-[280px] text-sm text-white/70">
                                    Перезвоним, уточним задачу, рассчитаем стоимость и подберём материалы с доставкой.
                                </p>
                            </div>

                            <div class="grid gap-3 text-sm">
                                <div class="flex items-center gap-3">
                                    <span class="h-2.5 w-2.5 rounded-full bg-[#ffba00]"></span>
                                    Быстрый расчёт стоимости
                                </div>
                                <div class="flex items-center gap-3">
                                    <span class="h-2.5 w-2.5 rounded-full bg-[#ffba00]"></span>
                                    Подбор материалов под объект
                                </div>
                                <div class="flex items-center gap-3">
                                    <span class="h-2.5 w-2.5 rounded-full bg-[#ffba00]"></span>
                                    Доставка по Москве и области
                                </div>
                            </div>
                        </div>

                        <div class="absolute -bottom-16 -right-16 h-52 w-52 rounded-full border-[28px] border-[#ffba00]/20"></div>
                    </div>
                </OnlyDesktop>

                <div
                    v-if="!callbackStore.base_form.successfully_sent"
                    class="w-full max-w-sm mx-auto p-3 sm:p-5"
                >
                    <p class="text-2xl text-[#2D2D2D] font-bold text-center mb-8">
                        {{ callbackStore.base_form.title }}
                    </p>

                    <div>
                        <div class="relative z-0 w-full group">
                            <input
                                type="tel"
                                @keyup="updatePhone($event.target.value)"
                                :value="callbackStore.base_form.data.phone"
                                v-imask="phoneMask"
                                name="ptf_phone"
                                id="ptf_phone"
                                class="block py-2.5 px-0 w-full text-gray-900 bg-transparent border-0 border-b-2 border-black appearance-none focus:outline-none focus:ring-0 focus:border-black peer"
                                :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['phone'] ? 'border-red-500' : '']"
                                placeholder=" "
                                required
                            />
                            <label
                                for="ptf_phone"
                                class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 peer-focus:text-black peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0"
                            >
                                Номер телефона
                            </label>
                        </div>

                        <CallbackErrorMessage
                            :error="callbackStore.base_form.errors"
                            :error-msg="'phone'"
                        />
                    </div>

                    <div class="mt-5">
                        <div class="relative z-0 w-full group">
                            <input
                                v-model="callbackStore.base_form.data.full_name"
                                type="text"
                                name="ptf_full_name"
                                id="ptf_full_name"
                                class="block py-2.5 px-0 w-full text-gray-900 bg-transparent border-0 border-b-2 border-black appearance-none focus:outline-none focus:ring-0 focus:border-black peer"
                                :class="[callbackStore.base_form.errors && callbackStore.base_form.errors['full_name'] ? 'border-red-500' : '']"
                                placeholder=" "
                                required
                            />
                            <label
                                for="ptf_full_name"
                                class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 peer-focus:text-black peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0"
                            >
                                Как к вам обращаться?
                            </label>
                        </div>

                        <CallbackErrorMessage
                            :error="callbackStore.base_form.errors"
                            :error-msg="'full_name'"
                        />
                    </div>

                    <div class="relative w-full lg:w-auto flex justify-center mt-10">
                        <button
                            @click.stop="callbackStore.sendPopupForm"
                            :disabled="callbackStore.base_form.sending"
                            class="flex justify-center items-center w-full border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-medium rounded-full text-sm px-14 py-2.5 focus:outline-none transition cursor-pointer"
                        >
                            <span v-if="!callbackStore.base_form.sending" class="text-base">
                                Заказать звонок
                            </span>
                            <span v-else class="text-base">
                                Отправляем
                            </span>
                            <SpinnerIndicator
                                v-if="callbackStore.base_form.sending"
                                class="w-6 h-6 ms-2"
                            />
                        </button>
                    </div>

                    <div class="mt-5">
                        <PrivacyPolicyAgreementLink/>
                    </div>
                </div>

                <div v-else class="w-full max-w-sm mx-auto p-3 sm:p-5 flex flex-col items-center">
                    <IconAnimatedCheckMark size="80"/>
                    <p class="text-lg font-medium text-center text-[#7ac142] mt-5">
                        Спасибо!<br/> Мы скоро свяжемся с вами.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
